import { commands, Extension } from '@tiptap/core';

/**
 * Types that carry their own alignment attribute — TextAlign's for text blocks and images, TableKit's
 * `align` for the cells. Keeping text-align in their style attribute as well would leave a
 * stale declaration to fight the toolbar.
 */
const SELF_ALIGNING_TYPES = [
  'heading',
  'paragraph',
  'image',
  'textDiv',
  'tableCell',
  'tableHeader'
];

const STYLED_TYPES = [
  'div',
  'section',
  'span',
  'listItem',
  'bulletList',
  'orderedList',
  'blockquote',
  'codeBlock',
  'table',
  'tableRow'
];

const withoutTextAlign = (style: string): string =>
  style
    .split(';')
    .map((s) => s.trim())
    .filter((s) => s && !s.toLowerCase().startsWith('text-align'))
    .join('; ');

const styleAttributes = (dropTextAlign: boolean) => ({
  style: {
    default: null as string | null,
    parseHTML: (element: HTMLElement) => {
      const style = element.getAttribute('style');
      if (!style) return null;
      return (dropTextAlign ? withoutTextAlign(style) : style.trim()) || null;
    },
    renderHTML: (attributes: Record<string, string | null>) => {
      let styleString = attributes.style || '';

      if (styleString && dropTextAlign) {
        styleString = withoutTextAlign(styleString);
      }

      if (attributes.backgroundColor) {
        const bgColorStyle = `background-color: ${attributes.backgroundColor}`;
        styleString = styleString ? `${styleString}; ${bgColorStyle}` : bgColorStyle;
      }

      return styleString ? { style: styleString } : {};
    }
  },
  id: {
    default: null as string | null,
    keepOnSplit: false,
    parseHTML: (element: HTMLElement) => element.getAttribute('id'),
    renderHTML: (attributes: Record<string, string | null>) => ({ id: attributes.id || undefined })
  },
  class: {
    default: null as string | null,
    parseHTML: (element: HTMLElement) => element.getAttribute('class'),
    renderHTML: (attributes: Record<string, string | null>) => ({
      class: attributes.class || undefined
    })
  },
  backgroundColor: {
    default: null as string | null,
    parseHTML: (element: HTMLElement) => {
      const style = element.getAttribute('style');
      if (style) {
        const match = style.match(/background-color:\s*([^;]+)/);
        if (match) return match[1].trim();
      }
      return null;
    },
    renderHTML: () => ({})
  }
});

export const StyleAttributeExtension = Extension.create({
  addCommands() {
    return {
      splitBlock:
        (options = {}) =>
        (props) => {
          const splitParent = props.tr.selection.$from.parent;
          const clearSplitID = splitParent.isTextblock && Boolean(splitParent.attrs.id);
          const preserveTextDiv = splitParent.type.name === 'textDiv';
          const splitAtStart = props.tr.selection.$from.parentOffset === 0;
          const split = commands.splitBlock(options)(props);
          if (!split || !props.dispatch || (!clearSplitID && !preserveTextDiv)) return split;

          const { $from } = props.tr.selection;
          const rightPos = $from.before();

          // TipTap turns an empty boundary half into its default paragraph. A styled text div
          // instead stays a text div on both sides, with its identity following the authored
          // content. Mid-text splits copy all attributes before keepOnSplit is consulted.
          if (preserveTextDiv) {
            const attrsWithoutID = { ...splitParent.attrs, id: null };
            if (splitAtStart) {
              const left = props.tr.doc.resolve(rightPos).nodeBefore;
              if (left) {
                props.tr.setNodeMarkup(rightPos - left.nodeSize, splitParent.type, attrsWithoutID);
              }
              props.tr.setNodeMarkup(rightPos, splitParent.type, splitParent.attrs);
            } else {
              props.tr.setNodeMarkup(rightPos, splitParent.type, attrsWithoutID);
            }
            return true;
          }

          if (splitAtStart) {
            const left = props.tr.doc.resolve(rightPos).nodeBefore;
            if (left?.attrs.id) {
              props.tr.setNodeMarkup(rightPos - left.nodeSize, undefined, {
                ...left.attrs,
                id: null
              });
            }
          } else if ($from.parent.attrs.id) {
            props.tr.setNodeMarkup(rightPos, undefined, { ...$from.parent.attrs, id: null });
          }
          return true;
        }
    };
  },

  addGlobalAttributes() {
    return [
      { types: SELF_ALIGNING_TYPES, attributes: styleAttributes(true) },
      // A centered cover carries its alignment in the style attribute and nowhere else, because
      // nothing above manages alignment for a div or a section.
      { types: STYLED_TYPES, attributes: styleAttributes(false) }
    ];
  }
});
